import { of, Subscription, throwError } from 'rxjs';
import { AjaxError, AjaxRequest, AjaxResponse } from 'rxjs/ajax';
import { filter, map, take } from 'rxjs/operators';
import { CimResultFormat, CimStream, CimStreamMonitorSetContext, CimStreamOptions, CimStreamRequestData } from '../data/cim-stream';
import { Http } from '../data/http';
import { Net } from '../data/net';
import { PowerShellCommand } from '../data/powershell';
import { PowerShellStream, PowerShellStreamMonitorSetContext, PowerShellStreamOptions } from '../data/powershell-stream';
import { WebsocketStreamDataRequestState } from '../data/websocket-stream';
import { Logging } from '../diagnostics/logging';
import { RpcObservablePerformanceClient } from '../rpc/performance/rpc-observable-performance-client';
import { RpcObservablePerformanceConfigClient } from '../rpc/performance/rpc-observable-performance-config-client';
import { RpcObservablePerformanceServer } from '../rpc/performance/rpc-observable-performance-server';
import { Rpc } from '../rpc/rpc';
import {
    PerformanceProfileData, PerformanceProfileDataCim, PerformanceProfileDataPowerShell, PerformanceProfileXhrFetch
} from './performance-profile-data';
import { PerformanceProfileDataType } from './performance-profile-data-type';
import { PerformanceProfileDatabase } from './performance-profile-database';
import { PerformanceProfileRecord } from './performance-profile-record';

interface PowerShellStreamContext {
    id: number;
    start: number;
    progressStart: number;
    progressEnd?: number;
    count: number;
    itemCount: number;
}

interface CimStreamContext {
    id: number;
    start: number;
    progressStart: number;
    progressEnd?: number;
    count: number;
    itemCount: number;
}

/**
 * Performance measurement class.
 */
export class PerformanceProfile {
    private static monitorName = 'PerformanceProfile';
    private static instance: PerformanceProfile;

    private database: PerformanceProfileDatabase = null;
    private rpc: Rpc;
    private rpcObservablePerformanceClient: RpcObservablePerformanceClient;
    private rpcObservablePerformanceServer: RpcObservablePerformanceServer;
    private subscription: Subscription;
    private moduleVersionMap: { [index: string]: string } = {};
    private rpcObservablePerformanceConfigClient: RpcObservablePerformanceConfigClient;

    /**
     * Gets the current PerformanceProfile instance.
     */
    public static get current(): PerformanceProfile {
        if (PerformanceProfile.instance) {
            return PerformanceProfile.instance;
        }

        PerformanceProfile.instance = new PerformanceProfile();
        return PerformanceProfile.instance;
    }

    public static get database(): PerformanceProfileDatabase {
        return PerformanceProfile.current.database;
    }

    /**
     * Record Route navigation performance measurement.
     */
    public static logRouteNavigation(
        source: string,
        start: number,
        end: number,
        url: string,
        target?: string,
        errorMessage?: string): void {
        const data: PerformanceProfileData = {
            source,
            start,
            end,
            errorMessage,
            type: PerformanceProfileDataType.RouteNavigation,
            routeNavigation: { url, target }
        };
        PerformanceProfile.current.log(data);
    }

    /**
     * Record Null packet.
     */
    public static logNull(source: string): void {
        const data: PerformanceProfileData = {
            source,
            start: 0,
            end: 0,
            errorMessage: null,
            type: PerformanceProfileDataType.Null
        };
        PerformanceProfile.current.logAnyway(data);
    }

    /**
     * Record XHR or Fetch performance measurement for PowerShell.
     */
    private static logXhrFetchPowerShell(
        source: string,
        start: number,
        end: number,
        url: string,
        method: string,
        status: number,
        powershell: PerformanceProfileDataPowerShell,
        errorMessage?: string): void {
        const data: PerformanceProfileData = {
            source,
            start,
            end,
            errorMessage,
            type: PerformanceProfileDataType.XhrFetch,
            xhrFetch: { url, method, status, powershell }
        };
        PerformanceProfile.current.log(data);
    }

    /**
     * Record XHR or Fetch performance measurement for CIM.
     */
    private static logXhrFetchCim(
        source: string,
        start: number,
        end: number,
        url: string,
        method: string,
        status: number,
        cim: PerformanceProfileDataCim,
        errorMessage?: string): void {
        const data: PerformanceProfileData = {
            source,
            start,
            end,
            errorMessage,
            type: PerformanceProfileDataType.XhrFetch,
            xhrFetch: { url, method, status, cim }
        };
        PerformanceProfile.current.log(data);
    }

    /**
     * Record XHR or Fetch performance measurement for Batch.
     */
    private static logXhrFetchBatch(
        source: string,
        start: number,
        end: number,
        url: string,
        method: string,
        status: number,
        batch: PerformanceProfileXhrFetch[],
        errorMessage?: string): void {
        const data: PerformanceProfileData = {
            source,
            start,
            end,
            errorMessage,
            type: PerformanceProfileDataType.XhrFetch,
            xhrFetch: { url, method, status, batch }
        };
        PerformanceProfile.current.log(data);
    }

    /**
     * Record XHR or Fetch performance measurement for general.
     */
    private static logXhrFetch(
        source: string,
        start: number,
        end: number,
        url: string,
        method: string,
        status: number,
        errorMessage?: string): void {
        const data: PerformanceProfileData = {
            source,
            start,
            end,
            errorMessage,
            type: PerformanceProfileDataType.XhrFetch,
            xhrFetch: { url, method, status }
        };
        PerformanceProfile.current.log(data);
    }

    /**
     * Record WebSocket performance measurement.
     */
    private static logWebSocketPowerShell(
        source: string,
        nodeName: string,
        command: string,
        context: PowerShellStreamContext,
        errorMessage?: string): void {
        const data: PerformanceProfileData = {
            source,
            start: context.progressStart,
            end: context.progressEnd,
            errorMessage,
            type: PerformanceProfileDataType.WebSocket,
            webSocket: { nodeName, id: context.id, count: context.count, itemCount: context.itemCount, powershell: { command } }
        };
        PerformanceProfile.current.log(data);
    }

    /**
     * Record WebSocket performance measurement.
     */
    private static logWebSocketCim(
        source: string,
        nodeName: string,
        cim: CimStreamRequestData,
        context: CimStreamContext,
        errorMessage?: string): void {
        const data: PerformanceProfileData = {
            source,
            start: context.progressStart,
            end: context.progressEnd,
            errorMessage,
            type: PerformanceProfileDataType.WebSocket,
            webSocket: { nodeName, id: context.id, count: context.count, itemCount: context.itemCount, cim }
        };
        PerformanceProfile.current.log(data);
    }

    private static powershellApi(
        url: string,
        body: string,
        response: { completed?: string, sessionId?: string; properties?: { completed: string; sessionId: string; }; }): PerformanceProfileDataPowerShell {
        const powershell: { command?: string; sessionId?: string; completed?: string } = {};
        if (body && body.indexOf('\"properties\"') > 0) {
            powershell.command = PerformanceProfile.getBetween(body, ',\"command\":\"', '\",\"')
                || PerformanceProfile.getBetween(body, '\"script\":\"##', '##:');
        }

        powershell.completed = response && response.completed;
        if (!powershell.completed) {
            powershell.completed = response && response.properties && response.properties.completed;
        }

        powershell.sessionId = response && response.sessionId;
        if (!powershell.sessionId) {
            powershell.sessionId = response && response.properties && response.properties.sessionId;
            if (!powershell.sessionId) {
                powershell.sessionId = PerformanceProfile.getBetween(url, '/pssessions/', '?');
            }
        }

        return powershell;
    }

    private static cimApi(url: string, body: string): PerformanceProfileDataCim {
        if (url.indexOf('/features/cim/query') > 0 || url.indexOf('/services/WinREST/CIM/query') > 0) {
            try {
                return JSON.parse(body);
            } catch {
                return null;
            }
        }

        const namespaceName = PerformanceProfile.getBetween(url, '/namespaces/', '/classes/');
        const className = PerformanceProfile.getBetween(url, '/classes/', '/instances');
        return { namespace: namespaceName, className: className };
    }

    private static batchApi(body: string, response: string): PerformanceProfileXhrFetch[] {
        const delimiter = '\x0d\x0a';
        const batch: PerformanceProfileXhrFetch[] = [];
        let last = body.indexOf(delimiter);
        let separator = body.substring(0, last + 2);
        const bodySegments = body.split(separator);
        last = response.indexOf(delimiter);
        separator = response.substring(0, last + 2);
        const responseSegments = response.split(separator);
        if (bodySegments.length === responseSegments.length && bodySegments.length > 1) {
            let batchMethod: string;
            let batchUrl: string;
            let batchBody: string;
            let batchStatus: number;
            let batchResponse: any;
            for (let i = 1; i < bodySegments.length; i++) {
                const bodySegment = bodySegments[i];
                const responseSegment = responseSegments[i];
                const bodySegmentLines = bodySegment.split(delimiter);
                let emptyLines = 0;
                for (let j = 0; j < bodySegmentLines.length; j++) {
                    const line = bodySegmentLines[j];
                    if (line.length > 0) {
                        if (emptyLines === 1 && !batchUrl) {
                            // reached header segment.
                            const query = line.split(' ');
                            batchMethod = query[0];
                            batchUrl = query[1];
                        } else if (emptyLines >= 2 && !batchBody) {
                            batchBody = line;
                        }
                    } else {
                        emptyLines++;
                    }
                }

                emptyLines = 0;
                const responseSegmentLines = responseSegment.split(delimiter);
                for (let j = 0; j < responseSegmentLines.length; j++) {
                    const line = responseSegmentLines[j];
                    if (line.length > 0) {
                        if (emptyLines === 1 && !batchStatus) {
                            // reached header segment.
                            const query = line.split(' ');
                            batchStatus = Number(query[1]);
                        } else if (emptyLines >= 2 && !batchResponse) {
                            try {
                                batchResponse = JSON.parse(line);
                            } catch {
                                batchResponse = {};
                            }
                        }
                    } else {
                        emptyLines++;
                    }
                }

                const batchItem: PerformanceProfileXhrFetch = {
                    url: batchUrl,
                    status: batchStatus,
                    method: batchMethod
                };
                if (batchUrl.indexOf('/features/powershellApi/') > 0 || batchUrl.indexOf('/services/WinREST/PowerShell/') > 0) {
                    batchItem.powershell = PerformanceProfile.powershellApi(batchUrl, batchBody, batchResponse);
                } else if (batchUrl.includes('/features/cim/query') || batchUrl.includes('/services/WinREST/CIM/')) {
                    batchItem.cim = PerformanceProfile.cimApi(batchUrl, batchBody);
                }

                batch.push(batchItem);
            }
        }

        return batch;
    }

    private static getBetween(source: string, begin: string, end: string): string {
        let index0 = source.indexOf(begin);
        if (index0 < 0) {
            return null;
        }

        index0 += begin.length;
        const index1 = source.indexOf(end, index0);
        if (index1 < 0) {
            return null;
        }

        return source.substring(index0, index1);
    }

    public enable(rpc: Rpc): void {
        MsftSme.setPerformanceProfile(true);
        this.registerRpc(rpc);
    }

    public disable(rpc: Rpc): void {
        MsftSme.setPerformanceProfile(false);
        this.registerRpc(rpc);
    }

    private checkPerformanceProfile(): boolean {
        if (!MsftSme.getPerformanceProfile()) {
            if (this.database) {
                this.database.close();
                this.database = null;
            }

            return false;
        }

        if (!this.database) {
            this.database = new PerformanceProfileDatabase();
        }

        return true;
    }

    public registerRpc(rpc: Rpc): void {
        if (!this.rpc) {
            this.rpc = rpc;
            if (MsftSme.isShell()) {
                this.subscription = rpc.stateChanged
                    .pipe(
                        filter(active => active),
                        take(1))
                    .subscribe(() => {
                        this.rpcObservablePerformanceConfigClient = new RpcObservablePerformanceConfigClient(this.rpc);
                        this.rpcObservablePerformanceServer = new RpcObservablePerformanceServer(rpc);
                        this.rpcObservablePerformanceServer.register(
                            request => {
                                if (request.type === PerformanceProfileDataType.Null) {
                                    // The packet is null packet and retain only the version data.
                                    if (request.sourceVersion === '0.2.0') {
                                        // this version can support config command when on/off switch was used.
                                        this.moduleVersionMap[request.sourceName] = request.sourceVersion;
                                    }

                                    return of(null);
                                }

                                if (!this.checkPerformanceProfile()) {
                                    return of(null);
                                }

                                return this.database.write(request).pipe(map(() => null));
                            });
                    });
            } else {
                this.subscription = rpc.stateChanged
                    .pipe(
                        filter(active => active),
                        take(1))
                    .subscribe(() => {
                        this.rpcObservablePerformanceClient = new RpcObservablePerformanceClient(rpc);
                    });
            }
        }

        this.updateMonitors();
    }

    public updateMonitors(): void {
        const enabled = this.checkPerformanceProfile();
        if (this.rpcObservablePerformanceConfigClient) {
            // enable/disable the performance profile data collection to current modules.
            // but these must be version 0.2.0.
            const items = this.rpc.rpcManager.getCurrentRpcOutbound();
            if (items) {
                for (const item of items) {
                    if (this.moduleVersionMap[item.name]) {
                        this.rpcObservablePerformanceConfigClient.config({ enabled }, item).subscribe();
                    }
                }
            }
        }

        if (enabled) {
            this.registerHttp();
            this.registerPowerShellStream();
            this.registerCimStream();
        } else {
            Http.unregisterMonitors(PerformanceProfile.monitorName);
            PowerShellStream.unregisterMonitors(PerformanceProfile.monitorName);
            CimStream.unregisterMonitors(PerformanceProfile.monitorName);
        }
    }

    private log(message: PerformanceProfileData): void {
        if (!this.checkPerformanceProfile()) {
            return;
        }

        const self = MsftSme.self();
        const record: PerformanceProfileRecord = {
            ...message,
            ...{ sessionId: self.Init.sessionId, timestamp: Date.now(), moduleName: self.Init.moduleName }
        };

        if (!record.errorMessage) {
            delete record['errorMessage'];
        }

        if (this.rpc && this.rpc.stateActive && this.rpcObservablePerformanceClient) {
            // send to shell.
            this.rpcObservablePerformanceClient.log(record).subscribe();
        } else {
            // write to database.
            this.database.write(record).subscribe();
        }
    }

    private logAnyway(message: PerformanceProfileData): void {
        const self = MsftSme.self();
        const record: PerformanceProfileRecord = {
            ...message,
            ...{ sessionId: self.Init.sessionId, timestamp: Date.now(), moduleName: self.Init.moduleName }
        };

        if (!record.errorMessage) {
            delete record['errorMessage'];
        }

        if (this.rpc && this.rpc.stateActive && this.rpcObservablePerformanceClient) {
            // send to shell.
            this.rpcObservablePerformanceClient.log(record).subscribe();
        }
    }

    private registerHttp(): void {
        const startPropertyName = '_start_';

        Http.registerMonitorSet({
            name: PerformanceProfile.monitorName,
            preMonitor: (request: AjaxRequest) => {
                if (!request) {
                    Logging.logWarning('Http', 'Http performance profile measurement error to access the AjaxRequest object.');
                    return of(request);
                }

                request[startPropertyName] = Date.now();
                return of(request);
            },
            successMonitor: (response: AjaxResponse<any>) => {
                if (!response || !response.request || !response.request.url) {
                    Logging.logWarning('Http', 'Http performance profile measurement error to access the AjaxResponse object.');
                    return of(response);
                }

                const request = response.request;
                const url = request.url;
                const method = request.method;
                const end = Date.now();
                const start = request[startPropertyName];
                if (url.indexOf('/features/powershellApi/') > 0 || url.indexOf('/services/WinREST/PowerShell/') > 0){
                    const powershell = PerformanceProfile.powershellApi(url, request.body, response.response);
                    PerformanceProfile.logXhrFetchPowerShell('Http', start, end, url, method, response.status, powershell);
                } else if (url.includes('/features/cim/') || url.includes('/services/WinREST/CIM')) {
                    const cim = PerformanceProfile.cimApi(url, request.body);
                    PerformanceProfile.logXhrFetchCim('Http', start, end, url, method, response.status, cim);
                } else if (url.indexOf('/api/batch') > 0) {
                    const batch = PerformanceProfile.batchApi(request.body, response.response);
                    PerformanceProfile.logXhrFetchBatch('Http', start, end, url, method, response.status, batch);
                } else {
                    PerformanceProfile.logXhrFetch('Http', start, end, url, method, response.status);
                }

                return of(response);
            },
            errorMonitor: (error: AjaxError) => {
                if (!error || !error.response || !error.request || !error.request.url) {
                    Logging.logWarning('Http', 'Http performance profile measurement error to access the AjaxError object.');
                    return throwError(() => error);
                }

                const request = error.request;
                const url = request.url;
                const method = request.method;
                const end = Date.now();
                const start = request[startPropertyName];
                const message = Net.getErrorMessage(error);
                if (url.indexOf('features/powershellApi') > 0 || url.indexOf('/services/WinREST/PowerShell/') > 0) {
                    const powershell = PerformanceProfile.powershellApi(url, request.body, error.response);
                    PerformanceProfile.logXhrFetchPowerShell('Http', start, end, url, method, error.status, powershell, message);
                } else if (url.includes('/features/cim/') || url.includes('/services/WinREST/CIM/')) {
                    const cim = PerformanceProfile.cimApi(url, request.body);
                    PerformanceProfile.logXhrFetchCim('Http', start, end, url, method, error.status, cim, message);
                } else {
                    PerformanceProfile.logXhrFetch('Http', start, end, url, method, error.status, message);
                }

                return throwError(() => error);
            }
        });
    }

    private registerPowerShellStream(): void {
        let masterId = 1;
        PowerShellStream.registerMonitorSet({
            name: PerformanceProfile.monitorName,
            preMonitor: (nodeName: string, command: PowerShellCommand, options?: PowerShellStreamOptions) => {
                const start = Date.now();
                const id = masterId++;
                const context: PowerShellStreamMonitorSetContext<PowerShellStreamContext> = {
                    nodeName,
                    command,
                    options,
                    data: { id, start, progressStart: start, count: 0, itemCount: 0 }
                };
                return of(context);
            },
            successMonitor: (response: any, context: PowerShellStreamMonitorSetContext<PowerShellStreamContext>) => {
                context.data.progressEnd = Date.now();
                context.data.count++;
                context.data.itemCount += (response && response.results && response.results.length || 1);
                PerformanceProfile.logWebSocketPowerShell(
                    'PowerShellStream',
                    context.nodeName,
                    context.command.command,
                    context.data);
                context.data.progressStart = Date.now();
                return of(response);
            },
            errorMonitor: (error: any, context: PowerShellStreamMonitorSetContext<PowerShellStreamContext>) => {
                context.data.progressEnd = Date.now();
                PerformanceProfile.logWebSocketPowerShell(
                    'PowerShellStream',
                    context.nodeName,
                    context.command.command,
                    context.data,
                    Net.getErrorMessage(error));
                return throwError(() => error);
            }
        });
    }

    private registerCimStream(): void {
        let masterId = 1;
        CimStream.registerMonitorSet({
            name: PerformanceProfile.monitorName,
            preMonitor: (
                nodeName: string,
                requestState: WebsocketStreamDataRequestState,
                request: CimStreamRequestData,
                format: CimResultFormat,
                options?: CimStreamOptions) => {
                const start = Date.now();
                const id = masterId++;
                const context: CimStreamMonitorSetContext<CimStreamContext> = {
                    nodeName,
                    requestState,
                    request,
                    format,
                    options,
                    data: { id, start, progressStart: start, count: 0, itemCount: 0 }
                };
                return of(context);
            },
            successMonitor: (response: any, context: CimStreamMonitorSetContext<CimStreamContext>) => {
                context.data.progressEnd = Date.now();
                context.data.count++;
                context.data.itemCount += (response && response.results && response.results.length || 1);
                PerformanceProfile.logWebSocketCim(
                    'CimStream',
                    context.nodeName,
                    this.removeCimStreamDetailData(context.request),
                    context.data);
                context.data.progressStart = Date.now();
                return of(response);
            },
            errorMonitor: (error: any, context: CimStreamMonitorSetContext<CimStreamContext>) => {
                context.data.progressEnd = Date.now();
                PerformanceProfile.logWebSocketCim(
                    'CimStream',
                    context.nodeName,
                    this.removeCimStreamDetailData(context.request),
                    context.data,
                    Net.getErrorMessage(error));
                return throwError(() => error);
            }
        });
    }

    private removeCimStreamDetailData(request: CimStreamRequestData): CimStreamRequestData {
        const skipKeys = ['data', 'keyProperties'];
        const requestRaw: any = request;
        const keys = Object.keys(requestRaw);
        const trimmedKeys = keys.filter(key => skipKeys.indexOf(key) < 0);
        if (keys.length !== trimmedKeys.length) {
            const copy: any = {};
            for (const key of trimmedKeys) {
                copy[key] = requestRaw[key];
            }

            return copy;
        }

        return request;
    }
}
